import { Alert, Stack, Box } from '@mui/material';
import { MuiFileInput } from 'mui-file-input';
import { FC, useEffect, useRef, useState, useContext } from 'react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { Application } from '@features/applications';
import MessagesList from '@features/messaging/components/MessagesList/MessagesList';
import SavedMessagesSelect from '@features/savedMessages/components/SavedMessagesSelect/SavedMessagesSelect';
import EnterpriseSendMessageButtons from '../EnterpriseSendMessageButtons/EnterpriseSendMessageButtons';
import { useGetEnterpriseThread } from '@features/messaging/hooks/useGetEnterpriseThread';
import { authContext } from '@staffedupusa/shared-components';
import BundledEditor from '@utils/BundledEditor';
import AIPromptModal from '../AIPromptModal/AIPromptModal';

type EnterpriseMessagingFormProps = {
  application?: Application;
  threadId?: number;
  applicantId?: number;
  companyId?: number;
  fullHeight?: boolean;
  disabledUnreadButton?: boolean;
  showMarkReadButton?: boolean;
  onUnreadSuccess: () => void;
  handlePageNo?: (newPageNo: number) => void;
};

const EnterpriseMessagingForm: FC<EnterpriseMessagingFormProps> = ({
  application,
  threadId,
  disabledUnreadButton,
  applicantId,
  companyId,
  fullHeight,
  showMarkReadButton,
  onUnreadSuccess,
  handlePageNo,
}) => {
  const [message, setMessage] = useState('');
  const [errorText, setErrorText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [threadPage, setThreadPage] = useState(1);
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);
  const editorRef = useRef<TinyMCEEditor | null>(null);

  const { thread, messages, isLoadingThread } = useGetEnterpriseThread({
    applicantId,
    threadId,
    companyId,
    setErrorText,
    threadPage,
  });

  const {
    authData: { user },
  } = useContext(authContext);
  const loggedInUserId = user?.id;

  const onSendSuccess = () => {
    handlePageNo && handlePageNo(1);
    setMessage('');
    setFile(null);
    setThreadPage(1);
  };

  const handleInsertAIContent = (content: string) => {
    setMessage(content);
    setShowAiPromptModal(false);
  };

  useEffect(() => {
    setThreadPage(1);
  }, [application, threadId]);

  useEffect(() => {
    setMessage('');
    setErrorText('');
    setFile(null);
    setThreadPage(1);
  }, [threadId]);

  return (
    <Stack
      spacing={2}
      sx={{
        height: {
          xs: fullHeight ? 'calc(100vh - 88px)' : 'auto',
          sm: fullHeight ? '100vh' : 'auto',
        },
        justifyContent: 'flex-end',
      }}
    >
      {errorText && <Alert severity="error">{errorText}</Alert>}
      {messages && Boolean(messages.length) && (
        <>
          <MessagesList
            messages={messages}
            fullHeight={fullHeight}
            headerData={thread}
            onEndReached={() => setThreadPage((prev) => prev + 1)}
            isLoading={isLoadingThread}
            loggedInUserId={loggedInUserId}
          />
        </>
      )}
      <Stack
        spacing={3}
        sx={{
          pt: 0,
          pb: 3,
          px: 3,
          borderRadius: 0,
          boxShadow: 'none',
          borderTop: '1px solid rgba(128, 129, 140, .25)',
        }}
        style={{ marginTop: '0' }}
      >
        <Box sx={{ pt: 3 }}>
          <SavedMessagesSelect
            setMessage={setMessage}
            message={message}
            company_id={thread?.company.id}
          />
        </Box>
        <BundledEditor
          onInit={(evt: any, editor: TinyMCEEditor | null) =>
            (editorRef.current = editor)
          }
          value={message}
          onEditorChange={(newValue: string) => setMessage(newValue)}
          init={{
            height: 180,
            menubar: false,
            plugins: ['lists', 'autolink', 'link', 'pagebreak'],
            toolbar:
              'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
            spellchecker_dialog: true,
            spellchecker_rpc_url: 'localhost/ephox-spelling',
            spellchecker_language: 'en_US',
            spellchecker_active: true,
            browser_spellcheck: true,
            contextmenu: false,
          }}
        />
        <MuiFileInput
          variant="outlined"
          size="small"
          label="Attach files (pdf, doc, docx, jpg, jpeg, png)"
          value={file!}
          onChange={(newFile) => setFile(newFile)}
          InputProps={{
            placeholder: 'true',
            inputProps: {
              accept: '.pdf, .doc, .docx, .jpg, .jpeg, .png',
              placeholder: 'false',
            },
          }}
        />
        <EnterpriseSendMessageButtons
          message={message}
          disabledUnreadButton={disabledUnreadButton}
          showMarkReadButton={showMarkReadButton}
          threadId={threadId ?? thread?.id}
          companyId={companyId}
          applicantId={applicantId}
          setErrorText={setErrorText}
          onSendSuccess={onSendSuccess}
          file={file}
          application={application}
          recipientId={thread?.participant1?.id}
          onUnreadSuccess={onUnreadSuccess}
          setShowAiPromptModal={setShowAiPromptModal}
        />
      </Stack>
      {showAiPromptModal && (
        <AIPromptModal
          isOpen={showAiPromptModal}
          handleInsertAIContent={handleInsertAIContent}
          handleClose={() => setShowAiPromptModal(false)}
        />
      )}
    </Stack>
  );
};

export default EnterpriseMessagingForm;
